// src/components/ElectricalSystemItemBox.js
"use client"

import React, { useEffect, useState, useMemo } from "react"

const ElectricalSystemItemBox = ({
  systemSections,
  systemItems,
  onAddOrUpdateLine,
  editingLine,
  onCancelEdit,
}) => {
  const [sectionId, setSectionId] = useState("")
  const [itemId, setItemId] = useState("")
  const [units, setUnits] = useState(100)

  useEffect(() => {
    if (editingLine) {
      setSectionId(editingLine.sectionId ? String(editingLine.sectionId) : "")
      setItemId(editingLine.itemId ? String(editingLine.itemId) : "")
      setUnits(editingLine.units)
    } else {
      setSectionId("")
      setItemId("")
      setUnits(100)
    }
  }, [editingLine])

  const sectionItemsForSelected = useMemo(
    () =>
      systemItems.filter(
        (i) =>
          String(
            i.electrical_estimates_system_section_id ?? i.sectionId
          ) === sectionId
      ),
    [systemItems, sectionId]
  )

  const canSubmit = sectionId && itemId && units > 0

  const handleSubmit = () => {
    if (!canSubmit) return
    onAddOrUpdateLine({
      sectionId,
      itemId,
      units: Number(units),
    })
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm flex flex-col">
      <h2 className="mb-3 text-sm font-semibold text-slate-900">
        Add System Item
      </h2>

      <div className="flex-1 space-y-3">

        {/* SECTION */}
        <div className="space-y-1">
          <label
            htmlFor="section"
            className="block text-xs font-medium text-slate-700"
          >
            Section / System Type
          </label>
          <select
            id="section"
            value={sectionId}
            onChange={(e) => {
              setSectionId(e.target.value)
              setItemId("")
            }}
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-xs"
          >
            <option value="">Select section</option>
            {systemSections.map((s) => (
              <option key={s.id} value={s.id}>
                {s.type}
              </option>
            ))}
          </select>
        </div>

        {/* ITEM */}
        <div className="space-y-1">
          <label
            htmlFor="system-item"
            className="block text-xs font-medium text-slate-700"
          >
            System Item
          </label>
          <select
            id="system-item"
            value={itemId}
            onChange={(e) => setItemId(e.target.value)}
            disabled={!sectionId}
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-xs disabled:bg-slate-100"
          >
            <option value="">Select item</option>
            {sectionItemsForSelected.map((item) => (
              <option key={item.id} value={item.id}>
                {item.item}{" "}
                {item.price != null
                  ? ` (Unit: $${Number(item.price).toFixed(2)})`
                  : ""}
              </option>
            ))}
          </select>
        </div>

        {/* UNITS */}
        <div className="space-y-1">
          <label
            htmlFor="units"
            className="block text-xs font-medium text-slate-700"
          >
            Units (ft / pieces)
          </label>
          <input
            id="units"
            type="number"
            min="1"
            step="1"
            value={units}
            onChange={(e) => {
              const val = parseFloat(e.target.value)
              setUnits(Number.isNaN(val) ? 0 : val)
            }}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-xs"
          />
        </div>

      </div>

      {/* BUTTONS AT BOTTOM */}
      <div className="mt-4">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="w-full rounded-md bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
        >
          {editingLine ? "✔ Update Item" : "➕ Add Item"}
        </button>

        {editingLine && (
          <button
            type="button"
            onClick={onCancelEdit}
            className="mt-2 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            ✖ Cancel Edit
          </button>
        )}
      </div>
    </div>
  )
}

export default ElectricalSystemItemBox
